import * as PIXI from 'pixi.js-legacy';
import _ from 'lodash';
import Base from './Base';
import InnerDropArea from './InnerDropArea';
import constants from '../settings/constants';
import { SHOW_CONF, PICKUP_ITEM, SCENE_REDRAWN } from '../settings/events';
import productService from '../services/products';
import proppingService from '../services/propping';
import {
  POINTER_DOWN,
  POINTER_MOVE,
  POINTER_UP,
  POINTER_UP_OUTSIDE,
} from '../util/supportedEvents';
import emitter from '../emitter';
import geometry from './util/geometry';
import getComponent from './util/getComponent';
import getDepthOffset from './util/getDepthOffset';
import tacHelpers from '../state/tac/tacHelpers';
import { getProppingBounds, topPropBounds } from '../services/products/models';
import project from './util/project';
import { mergePolygons } from '../util/mergePolygons';
import { isStandAlone } from '../services/products/models';
import { ITEMS } from '../constants';

const DRAGGING_THRESHOLD = 2;

const LEFT = 'left';
const RIGHT = 'right';

export default class Item extends Base {
  constructor(args) {
    super(args);

    this.app = args.app;
    this.origo = args.origo || { x: 0, y: 0 };
    this.onDragStart = args.onDragStart;
    this.pickUp = this.pickUp.bind(this);
    this.options = args.options;
    this.measurementsActive = args.options.measurementsActive;

    if (this.onDragStart) {
      this.interactive = true;
      this.cursor = 'pointer';
    }

    this.onHitAreaRequired = this.onHitAreaRequired.bind(this);
    this.db_onHitAreaRequired = _.debounce(this.onHitAreaRequired, 100, {
      leading: false,
      trailing: true,
    });

    emitter.on(SCENE_REDRAWN, this.db_onHitAreaRequired);

    this.on(POINTER_DOWN, this.onPointerDown);
    this.update(args);
  }

  destroy(args) {
    emitter.off(SCENE_REDRAWN, this.db_onHitAreaRequired);
    super.destroy(args);
  }

  onHitAreaRequired() {
    this.setHitArea();
  }

  //
  // Pointer events

  onPointerUp = (event, abort = false) => {
    if (!this._pointerPos || this.disableInteraction) {
      return;
    }

    this._pointerPos = null;

    this.off(POINTER_MOVE, this.onPointerMove);
    this.off(POINTER_UP, this.onPointerUp);
    this.off(POINTER_UP_OUTSIDE, this.onPointerUp);

    if (!abort && event.type !== POINTER_UP_OUTSIDE && !this._isDragging) {
      emitter.emit(SHOW_CONF, this);
    }
  };

  onPointerDown = event => {
    event.stopPropagation();

    if (this.wallResizerActive || this.disableInteraction) {
      return;
    }

    this.on(POINTER_UP, this.onPointerUp);
    this.on(POINTER_MOVE, this.onPointerMove);

    this._isDragging = false;

    if (
      productService.isSection(this.parentItem) &&
      this.parent &&
      this.parent.parent
    ) {
      this.parent.parent.on(POINTER_UP_OUTSIDE, this.onPointerUp);
    }

    this._pointerPos = {
      x: event.data.global.x,
      y: event.data.global.y,
    };
  };

  onPointerMove = event => {
    if (!event.data.buttons) {
      // moving without buttons means that user started
      // dragging too close to the border to pick it up,
      // and is now returning from a perceived failed drag
      this.onPointerUp(event, true);
    }

    if (!this._pointerPos || this._isDragging || this.disableInteraction) {
      return;
    }

    const currentX = event.data.global.x;
    const currentY = event.data.global.y;

    this._isDragging =
      Math.abs(currentX - this._pointerPos.x) > DRAGGING_THRESHOLD ||
      Math.abs(currentY - this._pointerPos.y) > DRAGGING_THRESHOLD;

    if (this._isDragging) {
      event.data.global = this._pointerPos;
      this.pickUp(event);
    }
  };

  pickUp(e) {
    let mode;
    let position;

    if (productService.isInsert(this.item) && !isStandAlone(this.item)) {
      this.dummy = true;
      mode = constants.DRAG_MODE.INSERT;
      position = this.toGlobal(new PIXI.Point(0, 0));
    } else if (this.parent && this.parent.parent && this.parent.parent.item) {
      // TODO fix above condition
      // this is a child item
      this.dummy = true;
      mode = constants.DRAG_MODE.MIXED;
      position = this.toGlobal(new PIXI.Point(0, 0));
    } else {
      mode = constants.DRAG_MODE.FLOAT;
      position = { x: this.x, y: this.y };
    }

    const offset = {
      x: e.data.global.x - position.x,
      y: e.data.global.y - position.y,
    };

    this.onDragStart({
      data: this.data,
      item: this.item,
      mode: mode,
      mouse: Object.assign({}, e.data.global),
      offset: offset,
      sprite: this,
    });

    emitter.emit(PICKUP_ITEM, this);
  }

  getAllChildren() {
    return this.childrenContainer.children;
  }

  getAllContainers() {
    return this.children.filter(child => this.isPureContainer(child));
  }

  getSelfHitArea() {
    const { scaledVertices } = this.data;
    let vertices;

    if (
      !geometry.extendsOutside(this.item, this.parentItem) &&
      this.parentItem.itemid
    ) {
      const padding = 4;
      vertices = [
        [scaledVertices[0][0] + padding, scaledVertices[0][1]],
        [scaledVertices[3][0] + padding, scaledVertices[3][1]],
        scaledVertices[7],
        [scaledVertices[2][0] - padding, scaledVertices[6][1]],
        [scaledVertices[2][0] - padding, scaledVertices[2][1]],
        [scaledVertices[1][0] - padding, scaledVertices[1][1]],
      ];
    } else {
      vertices = [
        scaledVertices[0],
        scaledVertices[1],
        scaledVertices[5],
        scaledVertices[6],
        scaledVertices[7],
        scaledVertices[3],
      ];
    }

    return {
      regions: [vertices],
    };
  }

  getHitArea() {
    let polygon = this.getSelfHitArea();
    this.getAllChildren()
      .filter(child => !this.excludeChildFromHitArea?.(child))
      .forEach(child => {
        const childRegions = child.getHitArea().polygon.regions;
        const regions = childRegions.map(region =>
          region.map(vertex => [
            vertex[0] + child.x + child.parent.x,
            vertex[1] + child.y + child.parent.y,
          ])
        );
        polygon = mergePolygons(polygon, {
          regions: regions,
        });
      });

    if (polygon.regions.length > 1) {
      const filler = this.getFiller(polygon.regions);
      polygon = mergePolygons(polygon, {
        regions: [filler],
      });
    }

    return {
      polygon,
    };
  }

  setHitArea() {
    const polygon = this.getHitArea().polygon;
    this.hitArea = new PIXI.Polygon([].concat(...polygon.regions?.[0]));
  }

  getResourceName(item) {
    const proppingBounds = getProppingBounds(item);
    if (this.shouldShowPropping(proppingBounds)) {
      if (!item.propping) {
        item.propping = proppingService.pickPropping({
          isDynamic: false,
          tac: this.app.tac,
          item,
        });
      }
      return `${item.id}_propping_${item.propping}`;
    } else {
      return item.id;
    }
  }

  shouldShowPropping(proppingBounds) {
    if (!this.options.showPropping || this.measurementsActive) {
      return false;
    }
    return proppingBounds.length > 0;
  }

  draw() {
    const { item, ratio } = this;
    const { outerSize } = this.data;
    this.sprite = this.sprite || new PIXI.Sprite();

    this.bracketContainers = this.bracketContainers || {
      left: new PIXI.Container(),
      right: new PIXI.Container(),
    };
    this.brackets = this.brackets || {};
    this.visible = !item.hidden;

    if (item.keepDuringDrag) {
      this.alpha = 0.5;
    } else {
      this.alpha = 1;
    }

    let resourceName;

    if (productService.isAddOnShelf(item)) {
      resourceName = `${item.id}:${
        item.x != null && item.x < 0 ? LEFT : RIGHT
      }`;
    } else {
      resourceName = this.getResourceName(item);
    }
    const resource = PIXI.Loader.shared.resources[resourceName];
    this.sprite.texture = resource.texture;

    this.sprite.width = outerSize.width * ratio;
    this.sprite.height = outerSize.height * ratio;
    if (item.propping) {
      const proppedBounds = getProppingBounds(item);
      if (this.shouldShowPropping(proppedBounds)) {
        const proppedSize = project.outer(
          proppedBounds[item.propping - 1].size
        );
        this.sprite.height = proppedSize.height * ratio;
      }
    }
    if (!this.bracketContainers[LEFT].parent) {
      this.addChild(this.bracketContainers[LEFT]);
    }
    this.drawBracket(LEFT);

    if (!this.sprite.parent) {
      this.addChild(this.sprite);
    }

    const { scaledVertices } = this.data;

    this.dropAreas = this.dropAreas || new PIXI.Container();
    this.dropAreas.x = scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.dropAreas.parent) {
      this.addChild(this.dropAreas);
    }

    this.childrenContainer = this.childrenContainer || new PIXI.Container();
    this.childrenContainer.x = scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.childrenContainer.parent) {
      this.addChild(this.childrenContainer);
    }
    this.drawChildren();
    if (!this.bracketContainers[RIGHT].parent) {
      this.addChild(this.bracketContainers[RIGHT]);
    }
    this.drawBracket(RIGHT);
  }

  // Override with an implementation as needed in ranges/items with brackets.
  drawBracket(side) {}

  _getProppingTop() {
    const proppedBounds = getProppingBounds(this.item);
    let proppingTop = 0;
    if (this.shouldShowPropping(proppedBounds)) {
      const proppingOffset = topPropBounds(this.item);
      proppingTop = proppingOffset.height;
    }
    return proppingTop;
  }

  place(x = this.item.x, y = this.item.y) {
    const { origo, data, parentItem, ratio } = this;

    if (this.item.z > 0) {
      const depthOffset = getDepthOffset(this.item);

      x -= depthOffset.x;
      y -= depthOffset.y;
    }
    const proppedBounds = getProppingBounds(this.item);
    if (this.shouldShowPropping(proppedBounds)) {
      const proppingOffset = topPropBounds(this.item);
      y += proppingOffset.height;
    }
    this.x =
      (x -
        (data.projectedVertices[4][0] - data.projectedVertices[0][0]) -
        origo.x) *
      ratio;
    this.y =
      (parentItem.height - (data.faceSize.height + y) + this.origo.y) * ratio;
  }

  update({ origo, ratio, item, parentItem, options }) {
    this.wallResizerActive = options.wallResizerActive;

    this.interactive = !options.wallResizerActive;

    if (
      (!!this.measurementsActive !== !!options.measurementsActive ||
        !!this.options.showPropping !== !!options.showPropping) &&
      this.bracketContainers
    ) {
      this.bracketContainers[LEFT].removeChildren();
      this.bracketContainers[RIGHT].removeChildren();
    }

    this.measurementsActive = options.measurementsActive;

    if (options && options !== this.options) {
      this.options = options;
    }

    if (ratio && this.ratio !== ratio) {
      this.ratio = ratio;
      if (this.bracketContainers) {
        this.bracketContainers[LEFT].removeChildren();
        this.bracketContainers[RIGHT].removeChildren();
      }
    }

    if (origo && origo !== this.origo) {
      this.origo = origo;
    }

    if (item && item !== this.item) {
      if (this.bracketContainers && item.width !== this.item.width) {
        this.bracketContainers[RIGHT].removeChildren();
      }

      this.item = item;
    }

    if (parentItem && parentItem !== this.parentItem) {
      this.parentItem = parentItem;
    }

    this.visible = true;
    this.calculateProjection();
    this.draw();
    this.place();
  }

  /*
  decide what container a new sprite belongs in
  */
  getContainer(sprite) {
    return this.childrenContainer;
  }

  addSprite(sprite, slot, tac) {
    const item = Object.assign({}, sprite.item);
    const parentPos = tacHelpers.getGlobalCoords(this.item, tac);

    const refitted = productService.getFit(item, slot);
    // product is null if no refit was done

    if (refitted) {
      refitted.x = slot.x - parentPos.x;
      refitted.y = slot.y - parentPos.y;
      refitted.z = slot.z - parentPos.z;
    }
    item.x = slot.x - parentPos.x;
    item.y = slot.y - parentPos.y;
    item.z = slot.z - parentPos.z;

    const fullFit = refitted
      ? tacHelpers.getSwitchableItem(item, refitted, {
          newParent: this.item,
        })
      : refitted;

    Object.assign(item, refitted, fullFit);

    sprite.update({
      item: item,
      parentItem: this.item,
      options: this.options,
      origo: { x: 0, y: 0 },
    });

    const container = this.getContainer(sprite);
    container.addChild(sprite);
    this.displayDropAreas(slot);

    sprite.once('added', () => {
      if (sprite.parent !== container) {
        this.displayDropAreas();
      }
    });

    this.sortChildren();

    return sprite;
  }

  /**
   * Cleans up an old sprite that no longer has a connection to a child.
   * Default implementation is to just destroy the sprite, but this is
   * overridden with additional logic in some item type classes.
   *
   * @param {Object} sprite
   * @return {undefined}
   */
  handleOldSprite(sprite) {
    sprite.destroy();
  }

  drawChildren() {
    const { item } = this;

    const oldSprites = this.sprites;

    this.sprites = new Map();

    if (Array.isArray(item.items)) {
      item.items.forEach(child => {
        /* We don't want brackets drawn here.
           They are drawn in drawBracket instead. */
        if (productService.isType(child, ITEMS.BRACKET)) return;

        const sprite = oldSprites && oldSprites.get(child.itemid);

        if (sprite && sprite.parent) {
          sprite.update({
            item: child,
            parentItem: item,
            options: this.options,
            ratio: this.ratio,
          });

          oldSprites.delete(child.itemid);
          this.sprites.set(child.itemid, sprite);
        } else {
          this.addItem(child);
        }
      });

      item.items.length && this.sortChildren();
    }
    if (oldSprites) {
      oldSprites.forEach(sprite => this.handleOldSprite(sprite));
    }
  }

  addItem(item) {
    const sprite = getComponent({
      options: this.options,
      item: item,
      parentItem: this.item,
      ratio: this.ratio,
      onDragStart: this.onDragStart,
      app: this.app,
    });
    this.getContainer(sprite).addChild(sprite);

    this.sprites.set(item.itemid, sprite);

    this.sortChildren();

    return sprite;
  }

  getDropAreaSprite(localSlot, width) {
    const localItem = {
      ...this.item,
      x: 0,
      y: 0,
    };
    if (geometry.collides(localSlot, localItem)) {
      const sprite = new InnerDropArea({
        width: width,
        height: localSlot.height * this.ratio,
        ratio: this.ratio,
        slot: localSlot,
      });

      sprite.slot = localSlot;

      sprite.x = 0;
      sprite.y =
        (this.item.height - (localSlot.y + localSlot.height)) * this.ratio;

      return sprite;
    }
  }

  drawDropAreas(slots) {
    slots = slots.filter(slot => slot.parent.itemid === this.item.itemid);

    const width = Math.floor(this.data.faceSize.width * this.ratio) - 2;

    slots.forEach(slot => {
      const localSlot = { ...slot, ...slot.local };
      localSlot.width = slot.width; // TODO does this break anything?
      const sprite = this.getDropAreaSprite(localSlot, width, slots);
      sprite && this.dropAreas.addChild(sprite);
    });
  }

  eraseDropAreas() {
    this.dropAreas.removeChildren();
    this.childrenContainer.children.forEach(child => child.eraseDropAreas());
  }

  displayDropAreas(slot) {
    this.displayDropArea(this.dropAreas, slot);
  }

  displayDropArea(dropAreas, slot) {
    const localSlot = slot && { ...slot, ...slot.local };
    dropAreas.children.forEach(dropArea => {
      this.toggleDropAreaVisibility(dropArea, localSlot);
    }, this);
  }

  toggleMultiParentChildren() {
    if (this.item && productService.isMultiParentProduct(this.item)) {
      this.visible = !this.visible;
    }

    this.getAllContainers().forEach(container => {
      container.children.forEach(grandChild => {
        grandChild.toggleMultiParentChildren &&
          grandChild.toggleMultiParentChildren();
      });
    });
  }

  toggleDropAreaVisibility(dropArea, localSlot) {
    dropArea.visible =
      dropArea.slot.y !== localSlot?.y || dropArea.slot.x !== localSlot?.x;
  }

  distance(a, b) {
    const x = a[0] - b[0];
    const y = a[1] - b[1];
    return x * x + y * y;
  }

  getFiller(regions) {
    const first = regions[0];
    const second = regions[1];
    const distances = [];
    first.forEach(start => {
      second.forEach(stop => {
        const res = {
          start,
          stop,
          distance: this.distance(start, stop),
        };

        distances.push(res);
      });
    });

    distances.sort((a, b) => a.distance - b.distance);

    const left = distances[0];
    const right = distances[1];

    const filler = [left.start, right.start, right.stop, left.stop];
    return filler;
  }
}
